import React, { useEffect } from 'react';
import { Box, useTheme, useMediaQuery, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthLogic } from '../hooks/useAuthLogic';
import LoginForm from './components/LoginForm';
import { useUniversityConfig } from '../../../contexts/UniversityConfigProvider';
import { useAuth } from '../../../contexts/AuthProvider';
import { useThemeMode } from '../../../contexts/ThemeProvider';
import AppLoader from '../../../components/common/AppLoader';
import Footer from '../ClassicTemplate/components/RightSection/Footer';

const Login: React.FC = () => {
  const { config } = useUniversityConfig();
  const { isAuthenticated, isLoading } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const navigate = useNavigate();
  const authLogic = useAuthLogic();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const footerConfig = config?.login?.footer || {};

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) return <AppLoader message="Checking authentication..." />;
  if (isAuthenticated) return null;

  const loginConfig = config?.login || {};
  const primaryColor = config?.theme?.primary || '#002465';
  const backgroundImageUrl = loginConfig.leftSection?.backgroundImage || '';
  const overlayColor = loginConfig.leftSection?.overlayColor || (isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.6)');

  const isForgotPasswordMode = authLogic.loginMode === authLogic.LOGIN_MODES.FORGOT_PASSWORD;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundImage: backgroundImageUrl ? `url("${backgroundImageUrl}")` : 'none',
        backgroundColor: backgroundImageUrl ? 'transparent' : (isDark ? '#1a1a1a' : '#ffffff'),
        position: 'relative',
        overflow: 'hidden',
        fontFamily: config?.theme?.fonts?.primary,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: overlayColor,
          zIndex: 0,
        }
      }}
    >
      <Box
        component="img"
        src="https://cdn.jsdelivr.net/gh/Falco-Peregrinus-Technologies-pvt-Ltd/falco-cdn-assets/assests/Watermark.png"
        alt="Watermark"
        sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: '85vh',
          width: 'auto',
          opacity: 0.5,
          zIndex: 1,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 4 },
          maxWidth: '450px',
          width: '100%',
          backgroundColor: isDark ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255)',
          borderRadius: 3,
          boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.5)' : '0 10px 40px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(8px)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(220, 220, 220, 0.7)',
        }}
      >
        {loginConfig.logo && (
          <Box
            component="img"
            src={loginConfig.logo}
            alt={`${config?.name || 'University'} Logo`}
            sx={{
              height: 85,
              mb: 3,
              filter: isDark ? 'brightness(1.1) contrast(1.1)' : 'none'
            }}
          />
        )}
        
        {!isForgotPasswordMode && (
          <>
            <Typography variant="h4" fontWeight="700" mb={1} color={isDark ? '#ffffff' : primaryColor} textAlign="center">
              {loginConfig.title || 'Welcome back'}
            </Typography>
            <Typography variant="body1" mb={4} color={isDark ? 'rgba(255,255,255,0.8)' : '#6b7280'} textAlign="center">
              {loginConfig.subtitle || 'Sign in to access your learning dashboard'}
            </Typography>
          </>
        )}

        <LoginForm authLogic={authLogic} config={config} />
        
        <Box
          sx={{
            mt: 3,
            pt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            width: '100%'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.35)',
              textShadow: isDark
                ? '0 1px 4px rgba(0,0,0,0.6)'
                : '0 1px 2px rgba(255,255,255,0.8)',
              userSelect: 'none',
            }}
          >
            {config?.theme?.poweredBy || 'Powered by Falco'}
          </Typography>
        </Box>
      </Box>

    </Box>
  );
};

export default Login;